import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { getPostPlainBody, postNo } from "@/entities/post/types";
import { DesktopHistorySidebar } from "@/features/history/components/DesktopHistorySidebar";
import { DesktopSearchToolbar } from "@/features/search/components/DesktopSearchToolbar";
import { SearchScopeSelect } from "@/features/search/components/SearchScopeSelect";
import type { SearchPageEvent } from "@/features/search/searchPageMediator";
import { resolveSearchResultThreadRoute } from "@/features/search/searchResultLink";
import type { SearchScope } from "@/features/search/searchScope";
import {
  type SearchPagePresentation,
  useSearchPagePresenter,
} from "@/features/search/useSearchPagePresenter";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Message } from "@/shared/ui/feedback";
import { getApiErrorMessage } from "@/shared/ui/feedback/apiErrorMessage";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";
import { Button, Input } from "@/shared/ui/form";

interface SearchQueryStateProps {
  children: ReactNode;
  error: unknown;
  hasData: boolean;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}

export const SearchQueryState: React.FunctionComponent<
  SearchQueryStateProps
> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying,
  onRetry,
}: SearchQueryStateProps) => (
  <QueryStateBoundary
    error={error}
    hasData={hasData}
    isLoading={isLoading}
    isRetrying={isRetrying}
    message={getApiErrorMessage(error, "検索に失敗しました")}
    onRetry={onRetry}
  >
    {children}
  </QueryStateBoundary>
);

export const SearchPage: React.FunctionComponent = () => {
  const presentation = useSearchPagePresenter();
  const { node, ...viewProps } = presentation;
  return (
    <ComponentScope node={node}>
      <SearchPageView {...viewProps} />
    </ComponentScope>
  );
};

export type SearchPageViewProps = Omit<SearchPagePresentation, "node">;

export const SearchPageView: React.FunctionComponent<SearchPageViewProps> = ({
  desktopHistorySidebarRef,
  input,
  query,
  scope,
  isDesktop,
  isSettingsOpen,
  searchQuery,
  onEvent,
}: SearchPageViewProps) => {
  const notify = (event: SearchPageEvent): void => {
    onEvent(event);
  };

  return (
    <section
      aria-labelledby="public-search-page-title"
      className={isDesktop ? "desktop-catalog-page" : "public-search-page"}
    >
      {isDesktop && (
        <DesktopSearchToolbar
          onToggleSidebar={(): void => {
            notify({ type: "search/sidebar-toggle-requested" });
          }}
          onScrollToTop={(): void => {
            notify({ type: "search/scroll-requested", edge: "top" });
          }}
          onScrollToBottom={(): void => {
            notify({ type: "search/scroll-requested", edge: "bottom" });
          }}
          onSettingsClick={(
            event: React.MouseEvent<HTMLAnchorElement>,
          ): void => {
            const hasModifier = hasLinkModifierKey(event);
            if (!hasModifier) event.preventDefault();
            notify({
              type: "search/settings-link-clicked",
              hasModifier,
            });
          }}
        />
      )}
      {isDesktop && (
        <h1 id="public-search-page-title" className="desktop-mode-title">
          投稿検索
        </h1>
      )}
      <div className={isDesktop ? "public-search-page" : undefined}>
        {!isDesktop && (
          <>
            <nav aria-label="投稿検索メニュー">
              <Link to="/">カタログ</Link>
              <Link to="/board">リスト</Link>
            </nav>
            <h1 id="public-search-page-title">掲示板全体の投稿検索</h1>
          </>
        )}
        <p>公開中のライブ投稿を新しい順に最大100件表示します。</p>
        <search aria-label="掲示板全体の投稿検索">
          <form
            onSubmit={(event: React.SubmitEvent<HTMLFormElement>): void => {
              event.preventDefault();
              notify({
                type: "search/submission-requested",
                value: input,
                scope,
              });
            }}
            className="public-search-form"
          >
            <SearchScopeSelect
              ariaLabel="掲示板全体の検索モード"
              scope={scope}
              onChange={(nextScope: SearchScope): void => {
                notify({
                  type: "search/scope-change-requested",
                  query,
                  scope: nextScope,
                });
              }}
            />
            <Input
              value={input}
              maxLength={100}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                notify({
                  type: "search/draft-changed",
                  sourceQuery: query,
                  value: event.currentTarget.value,
                });
              }}
              aria-label="掲示板全体の投稿検索"
              className="min-w-0 flex-1"
            />
            <Button
              type="submit"
              disabled={!input.trim() || searchQuery.isRetrying}
            >
              {searchQuery.isRetrying ? "検索中" : "検索"}
            </Button>
          </form>
        </search>
        {/*
          検索語が空のときクエリは enabled:false で走らない。この状態を
          QueryStateBoundary へ渡すと data も error も loading もない状態として
          「データを読み込めませんでした」に落ちるため、境界の外で分岐する。
        */}
        {!query.trim() ? (
          <Message variant="info">検索語を入力してください</Message>
        ) : (
          <SearchQueryState
            error={searchQuery.error}
            hasData={searchQuery.hasData}
            isLoading={searchQuery.isLoading}
            isRetrying={searchQuery.isRetrying}
            onRetry={(): void => {
              notify({ type: "search/retry-requested" });
            }}
          >
            {searchQuery.data?.posts.length === 0 ? (
              <Message variant="info">一致する公開投稿はありません</Message>
            ) : (
              <ol className="public-search-results" aria-live="polite">
                {searchQuery.data?.posts.map((post) => (
                  <li key={post.id}>
                    <SearchResultThreadLink
                      threadId={post.threadId}
                      postId={post.id}
                    >
                      No.{postNo(post)} / スレッドへ
                    </SearchResultThreadLink>
                    <p>{getPostPlainBody(post)}</p>
                  </li>
                ))}
              </ol>
            )}
          </SearchQueryState>
        )}
      </div>
      {isDesktop && (
        <>
          <DesktopHistorySidebar
            ref={desktopHistorySidebarRef}
            showClosedHandle={false}
          />
          <DesktopSettingsModal
            isOpen={isSettingsOpen}
            onClose={(): void => {
              notify({ type: "search/settings-close-requested" });
            }}
          />
        </>
      )}
    </section>
  );
};

function SearchResultThreadLink({
  children,
  postId,
  threadId,
}: {
  children: React.ReactNode;
  postId: string;
  threadId: string;
}): React.ReactNode {
  const route = resolveSearchResultThreadRoute(threadId, postId);
  if (route.to === "/$slug/thread/$threadId") {
    return (
      <Link
        to={route.to}
        params={route.params}
        hash={route.hash}
        hashScrollIntoView={{ block: "center" }}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      to={route.to}
      params={route.params}
      hash={route.hash}
      hashScrollIntoView={{ block: "center" }}
    >
      {children}
    </Link>
  );
}
